import { useEffect, useRef, useState } from 'react'
import { NiceSailboatSVG } from './ui/NiceSailboatSVG'

interface WakeSegment {
  x: number
  y: number
  angle: number       // radians, direction the segment points (roughly boat heading)
  length: number      // px
  width: number       // base width in px
  life: number        // ms
  maxLife: number     // ms
  amplitude: number   // wobble amplitude in px
  seed: number        // random phase so segments don't all wobble the same
}

export function SailboatWithRipples() {
  const [isEnabled, setIsEnabled] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const boatRef = useRef<HTMLDivElement>(null)        // OUTER: position + rotation
  const boatInnerRef = useRef<HTMLDivElement>(null)    // INNER: left/right mirroring
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rafRef = useRef<number | null>(null)

  const targetPosition = useRef({ x: 0, y: 0 })
  const currentPosition = useRef({ x: 0, y: 0 })
  const lastPosition    = useRef({ x: 0, y: 0 })
  const velocity        = useRef({ x: 0, y: 0 })

  const isVisible       = useRef(false)
  const isReducedMotion = useRef(false)
  const isPointerFine   = useRef(false)
  const lastUpdateTime  = useRef(0)
  const lastRippleTime  = useRef(0)

  // Which way we face (with a bit of hysteresis so it doesn't flicker at low speeds)
  const facingLeft = useRef(false)

  const wake = useRef<WakeSegment[]>([])

  const lerp = (s: number, e: number, f: number) => s + (e - s) * f
  const clamp = (v: number, mi: number, ma: number) => Math.min(Math.max(v, mi), ma)

  // Utility to cap array
  const pushCapped = <T,>(arr: T[], item: T, cap: number) => {
    arr.push(item)
    if (arr.length > cap) arr.shift()
  }

  // Add a wake segment (emits two ribbons in a V)
  const addWakeSegment = (emitX: number, emitY: number, headingRad: number, speed: number) => {
    // throttle emission & scale by speed
    const spread = 0.35;                   // V angle (radians) ~ 20°
    const backOffset = 12;                 // start a bit behind boat
    const baseLen = 140;                   // length baseline - longer ribbons
    const baseWidth = 14;                  // width baseline
    const maxCap = 96;                     // max active segments - keep more alive

    // Slightly behind the boat along opposite of heading
    const sx = emitX - Math.cos(headingRad) * backOffset
    const sy = emitY - Math.sin(headingRad) * backOffset

    // Scale with speed a little (clamped) - keeps it longer across speeds
    const sp = Math.min(Math.max(speed, 0), 18)
    const len = baseLen * (0.9 + sp / 18 * 0.9)   // 0.9–1.8x - longer across all speeds
    const wid = baseWidth * (0.6 + sp / 18 * 0.6) // 0.6–1.2x

    const mk = (ang: number): WakeSegment => ({
      x: sx,
      y: sy,
      angle: ang,
      length: len,
      width: wid,
      life: 0,
      maxLife: 3400 + Math.random() * 1400,     // 2.4–3.8s - much longer life
      amplitude: 3 + Math.random() * 2.5,      // 3–5.5px wobble
      seed: Math.random() * Math.PI * 2
    })

    // left & right arms of the V
    pushCapped(wake.current, mk(headingRad + spread), maxCap)
    pushCapped(wake.current, mk(headingRad - spread), maxCap)
  }

  // Advance life & fade
  const updateWake = (dt: number) => {
    wake.current = wake.current.filter(seg => {
      seg.life += dt
      return seg.life < seg.maxLife
    })
  }

  // Draw tapered, wavy ribbons
  const renderWake = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // draw from oldest to newest so overlap feels right
    for (let i = 0; i < wake.current.length; i++) {
      const seg = wake.current[i]
      const p = seg.life / seg.maxLife            // 0..1
      const alpha = (1 - p * 0.6) * 0.26          // fades slower
      if (alpha <= 0.01) continue

      const length = seg.length * (1 - p * 0.08)  // shrinks much slower
      const width  = seg.width  * (0.85 - p * 0.65) // doesn't taper to zero too fast
      const amp    = seg.amplitude * (1 - p)      // wobble fades

      const dirx = Math.cos(seg.angle)
      const diry = Math.sin(seg.angle)
      const nx = -diry   // perpendicular
      const ny = dirx

      // build left & right edge points
      const steps = 16
      const left: Array<[number, number]> = []
      const right: Array<[number, number]> = []

      for (let k = 0; k <= steps; k++) {
        const t = k / steps
        const along = t * length
        const bx = seg.x - dirx * along
        const by = seg.y - diry * along

        // gentle sine wobble across the width, fades toward the tail
        const wobble = Math.sin(t * 8 + seg.seed) * amp * (1 - t)

        const halfW = (width * (1 - t)) * 0.5 // taper toward tail
        left.push([bx + (halfW + wobble) * nx, by + (halfW + wobble) * ny])
        right.push([bx - (halfW + wobble) * nx, by - (halfW + wobble) * ny])
      }

      // draw closed ribbon (left edge forward, right edge back)
      ctx.beginPath()
      ctx.moveTo(left[0][0], left[0][1])
      for (let k = 1; k < left.length; k++) ctx.lineTo(left[k][0], left[k][1])
      for (let k = right.length - 1; k >= 0; k--) ctx.lineTo(right[k][0], right[k][1])
      ctx.closePath()

      // soft fill + subtle outline
      ctx.globalCompositeOperation = 'lighter'
      ctx.fillStyle = `rgba(167,194,255,${alpha})` // your accent-contrast tint
      ctx.fill()

      ctx.globalCompositeOperation = 'source-over'
      ctx.lineWidth = 1
      ctx.strokeStyle = `rgba(27,59,122,${alpha * 0.5})`
      ctx.stroke()
    }
  }

  // Animation loop using requestAnimationFrame
  const animate = (t: number) => {
    if (!isVisible.current || !containerRef.current || !boatRef.current) {
      rafRef.current = requestAnimationFrame(animate)
      return
    }

    const dt = t - lastUpdateTime.current
    lastUpdateTime.current = t

    // Very slow follow
    const lerpFactor = 0.003
    currentPosition.current.x = lerp(currentPosition.current.x, targetPosition.current.x, lerpFactor)
    currentPosition.current.y = lerp(currentPosition.current.y, targetPosition.current.y, lerpFactor)

    // Velocity & ripples
    const dx = currentPosition.current.x - lastPosition.current.x
    const dy = currentPosition.current.y - lastPosition.current.y
    velocity.current.x = dx
    velocity.current.y = dy

    const moveD = Math.hypot(dx, dy)

    // Emit wake while moving (denser wake)
    if (moveD > 2 && t - lastRippleTime.current > 60) {
      const headingRad = Math.atan2(velocity.current.y, velocity.current.x)
      addWakeSegment(lastPosition.current.x, lastPosition.current.y, headingRad, moveD)
      lastRippleTime.current = t
    }

    lastPosition.current.x = currentPosition.current.x
    lastPosition.current.y = currentPosition.current.y

    // Clamp to viewport
    const vw = window.innerWidth
    const vh = window.innerHeight
    const x = clamp(currentPosition.current.x, 24, vw - 24)
    const y = clamp(currentPosition.current.y, 24, vh - 24)

    /* ---------- heading → mirror + small local rotation ---------- */
    const speed = moveD
    let headingDeg = Math.atan2(velocity.current.y, velocity.current.x) * 180 / Math.PI // -180..180

    // Update facingLeft only if moving enough so it doesn't flicker
    if (speed > 0.6) {
      const goingLeftNow = headingDeg > 90 || headingDeg < -90
      facingLeft.current = goingLeftNow
    }

    // Convert global heading to a small local angle relative to "forward"
    // Forward = right when not mirrored; Forward = left when mirrored.
    // If mirrored, subtract/ add 180 to bring it near 0.
    let localHeading = headingDeg
    if (facingLeft.current) {
      localHeading = headingDeg > 0 ? headingDeg - 180 : headingDeg + 180 // now ~[-90,90]
    }

    // Keep rotation subtle
    const limitedRotation = clamp(localHeading, -25, 25)

    // Gentle bob/rock
    const time = t * 0.03
    const bob  = Math.sin(time * 0.5) * 0.6 + Math.sin(time * 0.7) * 0.4
    const rockX = Math.sin(time * 0.3) * 0.1
    const rockY = Math.cos(time * 0.2) * 0.1

    // OUTER: translate + local rotate
    boatRef.current.style.transform =
      `translate3d(${x + rockX}px, ${y + bob + rockY}px, 0) rotate(${limitedRotation}deg)`

    // INNER: flip horizontally when going left
    if (boatInnerRef.current) {
      boatInnerRef.current.style.transform = `scaleX(${facingLeft.current ? -1 : 1})`
      boatInnerRef.current.style.transformOrigin = '50% 50%'
    }

    // Wake
    updateWake(dt)
    renderWake()

    rafRef.current = requestAnimationFrame(animate)
  }

  // Mouse move handler
  const handleMouseMove = (e: MouseEvent) => {
    targetPosition.current.x = e.clientX
    targetPosition.current.y = e.clientY
    isVisible.current = true
  }

  // Mouse leave handler - gentle idle drift
  const handleMouseLeave = () => {
    isVisible.current = false
    // Set target to a gentle drift position
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    targetPosition.current.x = viewportWidth * 0.3 + Math.sin(Date.now() * 0.0005) * 100
    targetPosition.current.y = viewportHeight * 0.2 + Math.cos(Date.now() * 0.0003) * 50
  }

  // Mouse enter handler
  const handleMouseEnter = () => {
    isVisible.current = true
  }

  // Touch handler for gentle idle drift
  const handleTouchMove = (e: TouchEvent) => {
    if (e.touches.length > 0) {
      const touch = e.touches[0]
      targetPosition.current.x = touch.clientX
      targetPosition.current.y = touch.clientY
      isVisible.current = true
    }
  }

  // Resize handler for canvas
  const handleResize = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    const dpr = window.devicePixelRatio || 1
    const rect = canvas.getBoundingClientRect()
    
    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.scale(dpr, dpr)
    }
  }

  useEffect(() => {
    // Check if sailboat is enabled from localStorage
    const savedBoat = localStorage.getItem('boat-enabled') !== 'false'
    setIsEnabled(savedBoat)
    
    // Listen for sailboat toggle events
    const handleSailboatToggle = (e: CustomEvent) => {
      setIsEnabled(e.detail.enabled)
    }
    
    window.addEventListener('boat-toggle', handleSailboatToggle as EventListener)
    
    // Check for reduced motion preference
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    isReducedMotion.current = reducedMotionQuery.matches

    const handleReducedMotionChange = (e: MediaQueryListEvent) => {
      isReducedMotion.current = e.matches
    }

    // Check for pointer type
    const pointerQuery = window.matchMedia('(pointer: fine)')
    isPointerFine.current = pointerQuery.matches

    const handlePointerChange = (e: MediaQueryListEvent) => {
      isPointerFine.current = e.matches
    }

    // Don't initialize if reduced motion or disabled
    if (isReducedMotion.current || !isEnabled) {
      return
    }

    // Set initial position
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    targetPosition.current.x = viewportWidth * 0.5
    targetPosition.current.y = viewportHeight * 0.3
    currentPosition.current.x = targetPosition.current.x
    currentPosition.current.y = targetPosition.current.y
    lastPosition.current.x = targetPosition.current.x
    lastPosition.current.y = targetPosition.current.y

    // Setup canvas
    handleResize()

    // Add event listeners
    if (isPointerFine.current) {
      document.addEventListener('mousemove', handleMouseMove, { passive: true })
      document.addEventListener('mouseleave', handleMouseLeave)
      document.addEventListener('mouseenter', handleMouseEnter)
    } else {
      // Touch devices get gentle drift
      document.addEventListener('touchmove', handleTouchMove, { passive: true })
    }

    window.addEventListener('resize', handleResize)
    reducedMotionQuery.addEventListener('change', handleReducedMotionChange)
    pointerQuery.addEventListener('change', handlePointerChange)

    // Start animation loop
    lastUpdateTime.current = performance.now()
    rafRef.current = requestAnimationFrame(animate)

    // Cleanup
    return () => {
      window.removeEventListener('boat-toggle', handleSailboatToggle as EventListener)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseleave', handleMouseLeave)
      document.removeEventListener('mouseenter', handleMouseEnter)
      document.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('resize', handleResize)
      reducedMotionQuery.removeEventListener('change', handleReducedMotionChange)
      pointerQuery.removeEventListener('change', handlePointerChange)
      
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [isEnabled])

  // Don't render if user prefers reduced motion or sailboat is disabled
  if (isReducedMotion.current || !isEnabled) {
    return null
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{
        willChange: 'transform',
        transform: 'translate3d(0, 0, 0)',
      }}
    >
      {/* Ripple Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{
          willChange: 'transform',
          transform: 'translate3d(0, 0, 0)',
        }}
      />
      
      {/* OUTER: pos + rotation */}
      <div
        ref={boatRef}
        className="absolute"
        style={{
          willChange: 'transform',
          transform: 'translate3d(0, 0, 0)',
        }}
      >
        {/* INNER: mirroring only */}
        <div ref={boatInnerRef}>
          <NiceSailboatSVG size={72} />
        </div>
      </div>
    </div>
  )
}
